import React, { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { ArrowLeft, ArrowRight, CheckCircle, User, Briefcase, Globe } from 'lucide-react';

interface OnboardingWizardProps {
  userRole: 'client' | 'therapist';
  onComplete: () => void;
}

interface FormData {
  // Common fields
  firstName: string;
  lastName: string;
  phone: string;
  
  // Therapist specific
  licenseNumber?: string;
  bio?: string;
  education?: string;
  yearsExperience?: number;
  hourlyRate?: number;
  languages?: string[];
  specialties?: string[];
  isOnlineAvailable?: boolean;
  isInPersonAvailable?: boolean;
  city?: string;
  country?: string;
  
  // Client specific
  preferences?: {
    preferredLanguage?: string;
    sessionType?: 'online' | 'in_person' | 'both';
    concerns?: string[];
  };
}

const STEPS = {
  client: [
    { id: 'personal', title: 'Información Personal', icon: User },
    { id: 'preferences', title: 'Preferencias', icon: Globe },
    { id: 'complete', title: 'Completado', icon: CheckCircle }
  ],
  therapist: [
    { id: 'personal', title: 'Información Personal', icon: User },
    { id: 'professional', title: 'Información Profesional', icon: Briefcase },
    { id: 'services', title: 'Servicios y Tarifas', icon: Globe },
    { id: 'complete', title: 'Completado', icon: CheckCircle }
  ]
};

const LANGUAGES = ['Español', 'English', 'Français', 'Deutsch', 'Português', 'Italiano'];
const CONCERNS = ['Ansiedad', 'Depresión', 'Estrés', 'Relaciones', 'Trabajo', 'Familia', 'Autoestima', 'Trauma'];

export const OnboardingWizard: React.FC<OnboardingWizardProps> = ({ userRole, onComplete }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    phone: '',
    languages: ['Español'],
    specialties: [],
    preferences: {
      concerns: []
    }
  });

  const steps = STEPS[userRole];
  const progress = ((currentStep + 1) / steps.length) * 100;

  const updateFormData = (field: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updatePreferences = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      preferences: { ...prev.preferences, [field]: value }
    }));
  };

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const saveProfile = async () => {
    if (!user) return;

    setLoading(true);
    try {
      // Update profiles table
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          first_name: formData.firstName,
          last_name: formData.lastName,
          phone: formData.phone,
        })
        .eq('id', user.id);

      if (profileError) throw profileError;

      // If therapist, create therapist record
      if (userRole === 'therapist') {
        const { error: therapistError } = await supabase
          .from('therapists')
          .insert({
            id: user.id,
            license_number: formData.licenseNumber || '',
            bio: formData.bio,
            education: formData.education,
            years_experience: formData.yearsExperience,
            hourly_rate: formData.hourlyRate,
            languages: formData.languages || ['Español'],
            is_online_available: formData.isOnlineAvailable || false,
            is_in_person_available: formData.isInPersonAvailable || false,
            city: formData.city,
            country: formData.country,
            is_active: true,
            is_verified: false // Will be verified by admin
          });

        if (therapistError) throw therapistError;
      }

      toast({
        title: 'Perfil completado',
        description: 'Tu perfil ha sido creado exitosamente',
      });

      onComplete();
    } catch (error: any) {
      console.error('Error saving profile:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudo guardar el perfil',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const renderPersonalStep = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="firstName">Nombre</Label>
          <Input
            id="firstName"
            value={formData.firstName}
            onChange={(e) => updateFormData('firstName', e.target.value)}
            placeholder="Tu nombre"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="lastName">Apellido</Label>
          <Input
            id="lastName"
            value={formData.lastName}
            onChange={(e) => updateFormData('lastName', e.target.value)}
            placeholder="Tu apellido"
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="phone">Teléfono (opcional)</Label>
        <Input
          id="phone"
          value={formData.phone}
          onChange={(e) => updateFormData('phone', e.target.value)}
          placeholder="+1 234 567 8900"
        />
      </div>
    </div>
  );

  const renderProfessionalStep = () => (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="licenseNumber">Número de Licencia</Label>
        <Input
          id="licenseNumber"
          value={formData.licenseNumber}
          onChange={(e) => updateFormData('licenseNumber', e.target.value)}
          placeholder="Tu número de licencia profesional"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="education">Educación</Label>
        <Textarea
          id="education"
          value={formData.education}
          onChange={(e) => updateFormData('education', e.target.value)}
          placeholder="Describe tu formación académica..."
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="bio">Biografía</Label>
        <Textarea
          id="bio"
          value={formData.bio}
          onChange={(e) => updateFormData('bio', e.target.value)}
          placeholder="Cuéntanos sobre ti y tu enfoque terapéutico..."
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="yearsExperience">Años de Experiencia</Label>
        <Input
          id="yearsExperience"
          type="number"
          value={formData.yearsExperience}
          onChange={(e) => updateFormData('yearsExperience', parseInt(e.target.value))}
          placeholder="0"
        />
      </div>
    </div>
  );

  const renderServicesStep = () => (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="hourlyRate">Tarifa por Hora (USD)</Label>
        <Input
          id="hourlyRate"
          type="number"
          value={formData.hourlyRate}
          onChange={(e) => updateFormData('hourlyRate', parseFloat(e.target.value))}
          placeholder="50"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="city">Ciudad</Label>
          <Input
            id="city"
            value={formData.city}
            onChange={(e) => updateFormData('city', e.target.value)}
            placeholder="Tu ciudad"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="country">País</Label>
          <Input
            id="country"
            value={formData.country}
            onChange={(e) => updateFormData('country', e.target.value)}
            placeholder="Tu país"
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Modalidades de Servicio</Label>
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="online"
              checked={formData.isOnlineAvailable}
              onCheckedChange={(checked) => updateFormData('isOnlineAvailable', checked)}
            />
            <Label htmlFor="online">Sesiones online</Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="inPerson"
              checked={formData.isInPersonAvailable}
              onCheckedChange={(checked) => updateFormData('isInPersonAvailable', checked)}
            />
            <Label htmlFor="inPerson">Sesiones presenciales</Label>
          </div>
        </div>
      </div>
    </div>
  );

  const renderPreferencesStep = () => (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Idioma Preferido</Label>
        <Select 
          value={formData.preferences?.preferredLanguage} 
          onValueChange={(value) => updatePreferences('preferredLanguage', value)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Selecciona un idioma" />
          </SelectTrigger>
          <SelectContent>
            {LANGUAGES.map((lang) => (
              <SelectItem key={lang} value={lang}>{lang}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>Tipo de Sesión Preferida</Label>
        <Select 
          value={formData.preferences?.sessionType} 
          onValueChange={(value) => updatePreferences('sessionType', value)}
        >
          <SelectTrigger>
            <SelectValue placeholder="Selecciona una modalidad" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="online">Solo online</SelectItem>
            <SelectItem value="in_person">Solo presencial</SelectItem>
            <SelectItem value="both">Ambas modalidades</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>Áreas de Interés (opcional)</Label>
        <div className="grid grid-cols-2 gap-2">
          {CONCERNS.map((concern) => (
            <div key={concern} className="flex items-center space-x-2">
              <Checkbox
                id={concern}
                checked={formData.preferences?.concerns?.includes(concern)}
                onCheckedChange={(checked) => {
                  const currentConcerns = formData.preferences?.concerns || [];
                  if (checked) {
                    updatePreferences('concerns', [...currentConcerns, concern]);
                  } else {
                    updatePreferences('concerns', currentConcerns.filter(c => c !== concern));
                  }
                }}
              />
              <Label htmlFor={concern} className="text-sm">{concern}</Label>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderCompleteStep = () => (
    <div className="text-center space-y-4">
      <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-green-100 flex items-center justify-center">
        <CheckCircle className="h-8 w-8 text-green-600" />
      </div>
      <h3 className="text-lg font-semibold">¡Perfil completado!</h3>
      <p className="text-muted-foreground">
        {userRole === 'therapist' 
          ? 'Tu perfil ha sido enviado para verificación. Te notificaremos cuando esté aprobado.'
          : 'Tu perfil está listo. ¡Ya puedes comenzar a buscar terapeutas!'
        }
      </p>
    </div>
  );

  const currentStepData = steps[currentStep];
  const Icon = currentStepData.icon;

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader className="text-center">
        <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
          <Icon className="h-6 w-6 text-primary" />
        </div>
        <CardTitle>{currentStepData.title}</CardTitle>
        <CardDescription>
          Paso {currentStep + 1} de {steps.length}
        </CardDescription>
        <Progress value={progress} className="mt-4" />
      </CardHeader>
      <CardContent className="space-y-6">
        {currentStepData.id === 'personal' && renderPersonalStep()}
        {currentStepData.id === 'professional' && renderProfessionalStep()}
        {currentStepData.id === 'services' && renderServicesStep()}
        {currentStepData.id === 'preferences' && renderPreferencesStep()}
        {currentStepData.id === 'complete' && renderCompleteStep()}
        
        <div className="flex justify-between pt-4">
          <Button
            onClick={prevStep}
            disabled={currentStep === 0}
            variant="outline"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Anterior
          </Button>
          
          {currentStep === steps.length - 1 ? (
            <Button onClick={onComplete} disabled={loading}>
              {loading ? 'Finalizando...' : 'Finalizar'}
            </Button>
          ) : currentStep === steps.length - 2 ? (
            <Button onClick={saveProfile} disabled={loading}>
              {loading ? 'Guardando...' : 'Completar Perfil'}
            </Button>
          ) : (
            <Button onClick={nextStep}>
              Siguiente
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};